import { createColumnHelper, useTable } from "@tanstack/react-table"
import { Suspense } from "react"

import { TableContext } from "#Contexts/TableContext"
import { useLocalStorage } from "#React/hooks/useLocalStorage"
import EmptyState from "#React/ui/Table/EmptyState"
import ErrorState from "#React/ui/Table/ErrorState"
import Header from "#React/ui/Table/Header"
import Headers from "#React/ui/Table/Headers"
import LoadingState from "#React/ui/Table/LoadingState"
import Rows from "#React/ui/Table/Rows"
import VirtualizedRows from "#React/ui/Table/VirtualizedRows"
import { cn } from "#Utils/cn"

import Button from "../Button/Button"
import Checkbox from "../Checkbox"
import ColumnFooters from "./ColumnFooters"
import { LoadMoreButton } from "./LoadMoreButton"
import { defaultTableFeatures } from "./tableFeatures"

const LazyColumnSettingsOverlay = React.lazy(() => import("#React/ui/Table/ColumnSettingsOverlay"))

const columnHelper = createColumnHelper()

const createExpanderColumn = (props = {}) => {
  return columnHelper.display({
    id: "expander",
    size: 30,
    header: () => null,
    cell: ({ row }) => {
      return row.getCanExpand() ? (
        <button
          {...{
            onClick: row.getToggleExpandedHandler(),
            className: "leading-none cursor-pointer text-sm text-gray-500 font-normal",
          }}
        >
          {row.getIsExpanded() ? (
            <i className="far fa-fw fa-chevron-down" />
          ) : (
            <i className="far fa-fw fa-chevron-right" />
          )}
        </button>
      ) : null
    },
    ...props,
  })
}

const CheckBox = ({ checked, onChange, indeterminate, disabled }) => {
  const handleChange = (val) => {
    onChange?.({ target: { checked: val } })
  }
  return (
    <Checkbox
      checked={checked}
      onCheckedChange={handleChange}
      disabled={disabled}
      indeterminate={indeterminate}
      data-testid="checkbox"
    />
  )
}

CheckBox.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  indeterminate: PropTypes.bool,
  disabled: PropTypes.bool,
}

// Append `columns-visibility` and keep `localStorageKey` empty for future improvements
// Maybe we want to persist certain column-widths in the future.
function getColumnPersistenceKey(key) {
  return key ? `${key}-columns-visibility` : null
}

const Table = ({
  columns: defaultColumns,
  data,
  fetchMore,
  metaData = {},
  loading,
  error,
  fetchMoreLoading,
  fetchMoreCompleted,
  defaultSorting,
  header,
  fixed = false,
  children,
  rowSelection,
  setRowSelection,
  inline = false,
  boxClassName,
  localStorageKey,
  onManualSort,
  selectionLimit,
  renderSubComponent,
  getRowCanExpand,
  ...rest
}) => {
  const bottom = React.useRef(null)

  const [persistedColumnVisibility, setPersistedColumnVisibility] = useLocalStorage(
    getColumnPersistenceKey(localStorageKey),
  )

  if (!defaultColumns) defaultColumns = []
  if (!fetchMore) fetchMore = () => {}

  const selectedRowsCount = rowSelection ? Object.keys(rowSelection).length : 0
  const isSelectionLimitReached = selectionLimit ? selectedRowsCount >= selectionLimit : false

  if (rowSelection && defaultColumns[0].id !== "selection") {
    defaultColumns.unshift(
      columnHelper.display({
        id: "selection",
        size: 40,
        header: ({ table }) => {
          const { selectionLimit } = table.options.meta

          if (selectionLimit) {
            return null
          }
          return (
            <CheckBox
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          )
        },
        cell: ({ row, table }) => {
          const { isSelectionLimitReached } = table.options.meta
          const disabled = isSelectionLimitReached && !row.getIsSelected()

          return (
            <CheckBox
              checked={row.getIsSelected()}
              disabled={disabled || !row.getCanSelect()}
              onChange={row.getToggleSelectedHandler()}
            />
          )
        },
      }),
    )
  }

  const [sorting, setSorting] = React.useState(defaultSorting ? [defaultSorting] : [])
  const [columns, setColumns] = React.useState(() => [...defaultColumns])
  const [columnOrder, setColumnOrder] = React.useState(() =>
    columns.map((column) => column.accessorKey || column.id),
  )
  const [columnVisibility, setColumnVisibility] = React.useState(() => {
    if (persistedColumnVisibility) {
      return persistedColumnVisibility
    }

    const initialVisibility = {}
    for (const col of columns) {
      if (col.defaultHidden) {
        const id = col.accessorKey || col.header
        initialVisibility[id] = false
      }
    }

    return initialVisibility
  })
  const tableContainerRef = React.useRef(null)
  const [tableContainerRefState, setTableContainerRefState] = React.useState(null)
  React.useEffect(() => {
    if (!tableContainerRef) {
      return
    }

    setTableContainerRefState(tableContainerRef)
  }, [tableContainerRef])

  // react-table doesn't pass the new sorting, but an updater function
  const handleSortingChange = React.useCallback(
    (updater) => {
      const newSorting = updater(sorting)

      if (onManualSort) {
        const manualSorting = onManualSort(newSorting)
        setSorting(manualSorting || newSorting)
      } else {
        setSorting(newSorting)
      }
    },
    [onManualSort, sorting],
  )

  if (!rowSelection) rowSelection = {}
  if (!setRowSelection) setRowSelection = () => {}

  // Custom row selection handler that respects the selection limit
  const handleRowSelectionChange = React.useCallback(
    (updater) => {
      if (!selectionLimit) {
        setRowSelection(updater)
        return
      }

      const newSelection = typeof updater === "function" ? updater(rowSelection) : updater
      const newSelectedCount = Object.keys(newSelection).length

      // Only apply the new selection if it doesn't exceed the limit
      if (newSelectedCount <= selectionLimit) {
        setRowSelection(updater)
      }
    },
    [rowSelection, setRowSelection, selectionLimit],
  )

  const table = useTable({
    features: defaultTableFeatures,
    data,
    columns,
    manualSorting: Boolean(onManualSort),
    getRowCanExpand,
    debugTable: false,
    debugAll: false,
    enableSortingRemoval: false,
    enableRowSelection: true,
    enableRowRangeSelection: false,
    onRowSelectionChange: handleRowSelectionChange,
    enableResizing: true,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnOrder,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onSortingChange: handleSortingChange,
    meta: {
      ...metaData,
      selectionLimit,
      selectedRowsCount,
      isSelectionLimitReached,
      columnOrder: columns,
      setColumnOrder: setColumns,
      renderSubComponent,
    },
    defaultColumn: {
      size: null,
      maxSize: null,
      minSize: null,
    },
    ...rest,
  })

  // When the column visibility changes, sync with local-storage
  React.useEffect(() => {
    if (columnVisibility) {
      setPersistedColumnVisibility(columnVisibility)
    }
  }, [columnVisibility, setPersistedColumnVisibility])

  React.useEffect(() => {
    const bottomCurrent = bottom.current

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !fetchMoreLoading && !fetchMoreCompleted) {
          fetchMore()
        }
      },
      { threshold: 0.5 },
    )

    if (bottomCurrent) {
      observer.observe(bottomCurrent)
    }
    return () => {
      if (bottomCurrent) {
        observer.unobserve(bottomCurrent)
      }
    }
  }, [fetchMore, loading, fetchMoreLoading, fetchMoreCompleted])

  return (
    <div
      className={cn("c-box overflow-auto", !inline && "h-full", boxClassName)}
      ref={tableContainerRef}
    >
      <TableContext.Provider
        value={{
          tableContainerRef: tableContainerRefState,
          table,
          loading,
          error,
          dataSize: data.length,
          selectionLimit,
          selectedRowsCount,
          isSelectionLimitReached,
          header,
        }}
      >
        {header}
        <table
          // has-[.empty-table]:h-[calc(100%-4em)] adds height for empty tables
          className={cn(
            "c-table border-separate tabular-nums has-[.empty-table]:h-[calc(100%-4em)]",
            fixed ? "table-fixed" : "table-auto",
          )}
        >
          {children}
        </table>
        <div ref={bottom} />
      </TableContext.Provider>
    </div>
  )
}

Table.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array.isRequired,
  fetchMore: PropTypes.func,
  metaData: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.object,
  fetchMoreLoading: PropTypes.bool,
  fetchMoreCompleted: PropTypes.bool,
  defaultSorting: PropTypes.object,
  header: PropTypes.node,
  fixed: PropTypes.bool,
  children: PropTypes.node,
  inline: PropTypes.bool,
  rowSelection: PropTypes.object,
  setRowSelection: PropTypes.func,
  boxClassName: PropTypes.string,
  rest: PropTypes.object,
  localStorageKey: PropTypes.string,
  onManualSort: PropTypes.func,
  selectionLimit: PropTypes.number,
  renderSubComponent: PropTypes.func,
  getRowCanExpand: PropTypes.func,
}

const TableDefaultStack = () => {
  return (
    <>
      <Table.Headers />
      <Table.Loading />
      <Table.Errors />
      <Table.Empty />
      <Table.Rows />
    </>
  )
}

const ColumnSettingsOverlayWithSuspense = (props) => (
  <Suspense
    fallback={
      <Button color="white" className="font-normal" disabled>
        Columns
      </Button>
    }
  >
    <LazyColumnSettingsOverlay {...props} />
  </Suspense>
)

Table.Headers = Headers
Table.Rows = Rows
Table.VirtualizedRows = VirtualizedRows
Table.Empty = EmptyState
Table.Errors = ErrorState
Table.Loading = LoadingState
Table.DefaultStack = TableDefaultStack
Table.Header = Header
Table.ColumnFooters = ColumnFooters
Table.LoadMoreButton = LoadMoreButton
Table.ColumnSettingsOverlay = ColumnSettingsOverlayWithSuspense

export { columnHelper, createExpanderColumn }
export default Table
